import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, type ButtonVariant } from './Button.js';

/**
 * Confirmation for anything that can't be taken back. Six actions in the
 * configuration pages had no guard at all (removing an inbox or a Google
 * identity, discarding a held report, removing the alert webhook, revoking
 * admin), and the one that did used window.confirm() with OK and Cancel
 * standing for two different affirmative outcomes.
 *
 * Hence `choices`, not a boolean: a question with two real answers gets two
 * named buttons plus Cancel, and each answer says what it does.
 *
 * Built on <dialog>.showModal(), which brings the focus trap, Escape and the
 * inert backdrop with it instead of reimplementing them.
 */
export interface ConfirmRequest<T> {
  title: string;
  body?: ReactNode | undefined;
  choices: { label: string; value: T; variant?: ButtonVariant | undefined }[];
  cancelLabel?: string | undefined;
}

interface Pending {
  request: ConfirmRequest<unknown>;
  resolve: (value: unknown) => void;
}

export function useConfirm(): {
  confirm: <T>(request: ConfirmRequest<T>) => Promise<T | null>;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<Pending | null>(null);
  const ref = useRef<HTMLDialogElement>(null);

  const confirm = useCallback(
    <T,>(request: ConfirmRequest<T>) =>
      new Promise<T | null>((resolve) => {
        setPending({
          request: request as ConfirmRequest<unknown>,
          resolve: resolve as (value: unknown) => void,
        });
      }),
    [],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (pending && !el.open) el.showModal();
    if (!pending && el.open) el.close();
  }, [pending]);

  const settle = (value: unknown) => {
    pending?.resolve(value);
    setPending(null);
  };

  const dialog = (
    <dialog
      ref={ref}
      className="ui-dialog"
      // Escape and the backdrop both mean "no": resolve null rather than
      // leaving the caller's promise hanging forever.
      onClose={() => settle(null)}
      onCancel={() => settle(null)}
    >
      {pending ? (
        <div className="ui-dialog__inner">
          <h2>{pending.request.title}</h2>
          {pending.request.body ? (
            <div className="ui-dialog__body">{pending.request.body}</div>
          ) : null}
          <div className="ui-dialog__actions">
            <Button variant="ghost" onClick={() => settle(null)}>
              {pending.request.cancelLabel ?? 'Cancel'}
            </Button>
            {pending.request.choices.map((c) => (
              <Button
                key={c.label}
                variant={c.variant ?? 'primary'}
                onClick={() => settle(c.value)}
              >
                {c.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
    </dialog>
  );

  return { confirm, dialog };
}
